import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

function PasswordField({ label, value, onChange, testId, toggleTestId }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">{label}</label>
      <div className="relative">
        <input
          data-testid={testId}
          type={show ? "text" : "password"}
          required minLength={6}
          value={value} onChange={onChange}
          className="eo-input pr-10"
        />
        <button
          type="button"
          data-testid={toggleTestId}
          onClick={() => setShow((v) => !v)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-[#5C5C54] hover:text-[#0A0A0A]"
          tabIndex={-1}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      if (!token) { setError("Missing reset token"); return; }
      try {
        const { data } = await api.get(`/auth/reset-password/verify/${token}`);
        setMeta(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid or expired reset link");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset — please sign in with your new password");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md eo-card p-8">
        <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80]">// Password Recovery</div>
        <h1 className="font-heading text-[28px] font-bold mt-1">Set a new password</h1>
        {error && <div className="mt-4 border border-[#E63946] bg-[#FBECEE] p-3 text-[13px] text-[#7A1620]">{error}</div>}
        {meta && !error && (
          <>
            <p className="text-[13px] text-[#5C5C54] mt-3">Resetting password for <b className="text-[#0A0A0A]">{meta.email}</b>.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <PasswordField
                label="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                testId="reset-password-input"
                toggleTestId="reset-password-toggle"
              />
              <PasswordField
                label="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                testId="reset-confirm-input"
                toggleTestId="reset-confirm-toggle"
              />
              <button data-testid="reset-submit-btn" disabled={busy} className="eo-btn-primary w-full">
                {busy ? "Updating..." : "Update password"}
              </button>
            </form>
          </>
        )}
        <div className="mt-6 pt-6 border-t border-[#DCD9D0] text-[12px] text-[#8A8A80]">
          <Link to="/login" className="text-[#1D3557] underline">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
